import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, X, Save } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';

const LIMIT = 20;

export const TransporterShipments: React.FC = () => {
  const { user } = useAuth();
  const [shipments, setShipments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [updateTarget, setUpdateTarget] = useState<any | null>(null);
  const [updateForm, setUpdateForm] = useState({ location: '', description: '' });
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = { transporterId: user?.id };
      if (search) filters.trackingNumber = search;
      const res = await apiClient.listShipments(filters, LIMIT, page * LIMIT);
      const d = res.data.data;
      setShipments(d?.shipments ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch {} finally { setLoading(false); }
  }, [user?.id, page, search]);

  useEffect(() => { load(); }, [load]);

  const openUpdate = (s: any) => {
    setUpdateTarget(s);
    setUpdateForm({ location: '', description: '' });
    setUpdateError('');
  };

  const submitUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!updateTarget) return;
    setUpdating(true); setUpdateError('');
    try {
      await apiClient.updateTracking(updateTarget.id, {
        status: 'IN_TRANSIT',
        location: updateForm.location || undefined,
        description: updateForm.description || undefined,
      });
      setUpdateTarget(null);
      load();
    } catch (err: any) {
      setUpdateError(err.response?.data?.error || 'Failed to update location');
    } finally { setUpdating(false); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">My Shipments</h1>
      </div>

      {/* Location update modal */}
      {updateTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 460 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontWeight: 600, fontSize: 16 }}>Update Location</h3>
              <button className="btn btn-ghost" style={{ padding: '4px 8px' }} onClick={() => setUpdateTarget(null)}><X size={16} /></button>
            </div>
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
              <span style={{ color: '#64748b' }}>Shipment: </span>
              <code style={{ fontWeight: 600 }}>{updateTarget.trackingNumber}</code>
              <span style={{ color: '#64748b', marginLeft: 12 }}>→ {updateTarget.receiverName}</span>
            </div>
            {updateError && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{updateError}</div>}
            <form onSubmit={submitUpdate}>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label><MapPin size={13} style={{ display: 'inline', marginRight: 4 }} />Current Location *</label>
                <input
                  required
                  value={updateForm.location}
                  onChange={e => setUpdateForm(p => ({ ...p, location: e.target.value }))}
                  placeholder="e.g. Kigali Hub, Nyarugenge"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label>Note (optional)</label>
                <input
                  value={updateForm.description}
                  onChange={e => setUpdateForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="e.g. Package arrived at sorting facility"
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="btn btn-primary" disabled={updating}>
                  <Save size={14} /> {updating ? 'Updating...' : 'Update Location'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setUpdateTarget(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              placeholder="Search tracking number..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              style={{ paddingLeft: 32, width: '100%' }}
            />
          </div>
        </div>

        {loading ? <div className="loading-spinner">Loading...</div> : shipments.length === 0 ? (
          <div className="empty-state"><h3>No shipments assigned</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Tracking #</th><th>Receiver</th><th>Destination</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {shipments.map((s: any) => (
                    <tr key={s.id}>
                      <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                      <td>{s.receiverName}</td>
                      <td>{s.receiverCity || '—'}</td>
                      <td><span className={`badge badge-${s.status?.toLowerCase().replace(/_/g, '-')}`}>{s.status}</span></td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <Link to={`/transporter/shipments/${s.id}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>View</Link>
                        {['DEPARTED', 'IN_TRANSIT'].includes(s.status) && (
                          <button className="btn btn-primary" style={{ padding: '3px 8px', fontSize: 12 }} onClick={() => openUpdate(s)}>
                            <MapPin size={12} /> Update Location
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
